import usePromptModal from './usePromptModal';
import React, { useEffect, useMemo, useState } from 'react';
import useFullDialog from './useFullDialog';
import { useStateRef } from '@op/hooks/common';
import useDefaultSummary, { RequestFuncMapType } from './useDefaultSummary';
import useRequestFeedsState from './useRequestFeedsState';
import { AiIconMap, DEFAULT_MODAL } from '../constants';
import { CommentsToggle } from '../components/CommentsToggle';

export default function usePageCardData({
  getGptShowChildren,
  requestFuncMap,
  filter,
  oriFeedsState,
  backStateData,
  getGptLoading,
  linkFilterChange,
  alwaysShowTitle,
  title,
  formatMessage,
  showModelType,
  showComments,
  onShowCommentsChange,
  hasComments,
}: {
  // 获取gpt是否处于，get gpt summary按钮和计算的状态
  getGptShowChildren?: (value: boolean) => void;
  // 进行前置请求的函数接口以及处理方式
  requestFuncMap?: RequestFuncMapType;
  // 过滤器条件，创建GPT的时候需要
  filter: any;
  // 外部的state数据
  oriFeedsState?: any;
  // 返回state接口的数据
  backStateData?: (data: any) => void;
  // 外部获取gpt的loading状态
  getGptLoading?: (value: boolean) => void;
  // 是否需要关联filter变化，默认为true
  linkFilterChange?: boolean;
  alwaysShowTitle?: boolean;
  // 标题
  title?: string;
  formatMessage: any;
  showModelType?: boolean;
  /** 是否显示评论内容 */
  showComments?: boolean;
  /** 评论显示状态变更回调 */
  onShowCommentsChange?: (checked: boolean) => void;
  /** 是否检测到评论元素 */
  hasComments?: boolean;
}) {
  const { setPromptInfo, promptInfo, visible, toggleVisible } = usePromptModal(
    {},
  );
  const { FullDialog, openFullDialog } = useFullDialog({});

  const [showChildren, setShowChildren] = useState(true);
  function changeShowChildren(value: boolean) {
    setShowChildren(value);
    getGptShowChildren?.(value);
  }

  const [showDefaultGptRef, showDefaultGpt, setShowDefaultGpt] = useStateRef(
    requestFuncMap !== undefined,
  );
  const {
    loading: stateLoading,
    requestFeedsStateData,
    total,
    feedsState,
  } = useRequestFeedsState({
    filter,
    needRequest: oriFeedsState === undefined,
    backStateData,
    oriFeedsState,
  });

  const { loading, defaultGptText, defaultGPT } = useDefaultSummary({
    filter,
    requestFuncMap,
    getGptLoading,
    requestFeedsStateData,
    stateLoading,
    linkFilterChange,
  });

  useEffect(() => {
    setShowDefaultGpt(requestFuncMap !== undefined);
  }, [requestFuncMap]);

  const titleInfo = useMemo(() => {
    if (typeof title === "boolean" && !title) return null;
    if (showChildren && !alwaysShowTitle) return '';
    const titleText = title || formatMessage({ id: 'AI Summary' });
    const icon = AiIconMap[promptInfo?.modelType || DEFAULT_MODAL];

    // 根据检测结果决定是否显示复选框
    const shouldShowCheckbox = hasComments;
    const checkboxElement = shouldShowCheckbox ? (
      <CommentsToggle
        checked={showComments}
        onChange={onShowCommentsChange}
        formatMessage={formatMessage}
      />
    ) : null;

    const titleContent = (
      <div className="dbo-ai-title-wrapper">
        <div className="dbo-ai-title">
          {icon && showModelType && <img src={icon} className="dbo-ai-title-icon" />}
          {titleText}
        </div>
        {checkboxElement}
      </div>
    );

    return titleContent;
  }, [promptInfo, showChildren, alwaysShowTitle, title, formatMessage, showModelType, hasComments, showComments, onShowCommentsChange]);

  return {
    setPromptInfo,
    promptInfo,
    visible,
    toggleVisible,
    FullDialog,
    openFullDialog,
    showChildren,
    showDefaultGptRef,
    showDefaultGpt,
    setShowDefaultGpt,
    loading,
    defaultGptText,
    defaultGPT,
    total,
    feedsState,
    changeShowChildren,
    titleInfo,
  };
}
